import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Switch } from "./ui/switch";
import { Eye, EyeOff } from "lucide-react";

const SalespersonModal = ({ salesperson, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "salesperson",
    isActive: true,
  });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (salesperson) {
      setFormData({
        name: salesperson.name || "",
        email: salesperson.email || "",
        password: "", // Don't populate password for editing
        role: salesperson.role || "salesperson",
        isActive:
          salesperson.isActive !== undefined ? salesperson.isActive : true,
      });
    }
  }, [salesperson]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.name || !formData.email) {
      alert("Please fill in all required fields");
      return;
    }

    // Validate password for new salesperson
    if (!salesperson && !formData.password) {
      alert("Password is required for new salesperson");
      return;
    }

    if (formData.password && formData.password.length < 6) {
      alert("Password must be at least 6 characters");
      return;
    }

    // Don't send password if empty (for editing)
    const submitData = { ...formData };
    if (!submitData.password) {
      delete submitData.password;
    }

    onSave(submitData);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900">
            {salesperson ? "Edit Salesperson" : "Create Salesperson"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Name */}
          <div>
            <Label
              htmlFor="name"
              className="text-sm font-semibold text-gray-900"
            >
              Full Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="John Doe"
              className="mt-2 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
              required
            />
          </div>

          {/* Email */}
          <div>
            <Label
              htmlFor="email"
              className="text-sm font-semibold text-gray-900"
            >
              Email Address <span className="text-red-500">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              placeholder="john.doe@example.com"
              className="mt-2 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
              required
            />
          </div>

          {/* Password */}
          <div>
            <Label
              htmlFor="password"
              className="text-sm font-semibold text-gray-900"
            >
              Password {!salesperson && <span className="text-red-500">*</span>}
            </Label>
            <div className="relative mt-2">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => handleChange("password", e.target.value)}
                placeholder={
                  salesperson
                    ? "Leave blank to keep unchanged"
                    : "Minimum 6 characters"
                }
                className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 pr-10"
                required={!salesperson}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            {salesperson && (
              <p className="text-xs text-gray-600 mt-2">
                Leave blank to keep current password
              </p>
            )}
          </div>

          {/* Role */}
          <div>
            <Label
              htmlFor="role"
              className="text-sm font-semibold text-gray-900"
            >
              Role <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.role}
              onValueChange={(value) => handleChange("role", value)}
            >
              <SelectTrigger className="mt-2 bg-white border-gray-300 text-gray-900">
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="salesperson">Salesperson</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="super_admin">Super Admin</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-600 mt-2">
              <strong>Salesperson:</strong> Can view and manage own leads
              <br />
              <strong>Admin:</strong> Can manage all leads and salespeople
              <br />
              <strong>Super Admin:</strong> Full system access
            </p>
          </div>

          {/* Active Status */}
          {salesperson && (
            <div className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg">
              <div>
                <Label className="text-sm font-semibold text-gray-900">
                  Active
                </Label>
                <p className="text-xs text-gray-600 mt-1">
                  Inactive users cannot log in or access the system
                </p>
              </div>
              <Switch
                checked={formData.isActive}
                onCheckedChange={(checked) => handleChange("isActive", checked)}
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="!border-gray-300 !text-gray-700 hover:!bg-gray-50"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="!bg-[#00C68D] hover:!bg-[#01C891] !text-white font-medium"
            >
              {salesperson ? "Update Salesperson" : "Create Salesperson"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SalespersonModal;
